import { FormGroup, FormGroupFeedback, IFormGroupProps, Intent, ITextAreaProps, TextArea } from '@pinpointhq/thumbtack';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField, useFormikContext } from 'formik';
import * as React from 'react';

interface ITextareaFieldProps {
  disabled?: boolean;
  name: string;
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
  growVertically?: boolean;
  submitOnEnter?: boolean;
  textareaProps?: Omit<ITextAreaProps, 'intent' | 'fill' | 'growVertically'>;
}

export function TextareaField<T extends string>({
  disabled = false,
  name,
  formGroupProps,
  growVertically = true,
  submitOnEnter = false,
  textareaProps,
}: ITextareaFieldProps) {
  const { submitForm } = useFormikContext();
  const [field, meta]: [FieldInputProps<T>, FieldMetaProps<T>, FieldHelperProps<T>] = useField<T>(name);
  const hasError = meta.touched && meta.error;

  /**
   * On Mac keyboards, the META key is represented by the "Command/Cmd" ("⌘") key.
   * I assume lowly windows power users would want to use CTRL+Enter to submit?
   * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/metaKey
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      if (submitOnEnter) submitForm();
    }
  };

  return (
    <FormGroup {...formGroupProps} labelFor={name}>
      <TextArea
        dir="auto"
        disabled={disabled}
        fill={true}
        growVertically={growVertically}
        onKeyDown={handleKeyDown}
        {...field}
        {...textareaProps}
        intent={hasError ? Intent.DANGER : Intent.NONE}
      />
      {hasError && <FormGroupFeedback intent={Intent.DANGER}>{meta.error}</FormGroupFeedback>}
    </FormGroup>
  );
}
